import React, { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { VisibleColumnSpec, WeightReportLineDetails } from "@/types";
import { useAuth } from "@/contexts/AuthContext";
import { useGetWeightReportLines } from "@/services/WeightReports";
import dayjs from "dayjs";

import SortableHeader from "@/components/molecules/SortableHeader";
import DataTable from "@/components/molecules/DataTable";
import Text from "@/components/atoms/Text";
import NumberFormatter from "@/components/atoms/NumberFormatter";
import MatchStatusLabel from "@/components/molecules/MatchStatusLabel";
import Group from "@/components/atoms/Group";
import { Menu } from "@mantine/core";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import WeightReportDrawer from "@/components/organisms/WeightReportDrawer";
import { useVisibleColumns } from "@/hooks/useVisibleColumns";

interface MatchWeightReportsTableProps {
  /** Define a column spec of what rows and columns to display and when **/
  visibleColumns?: VisibleColumnSpec<WeightReportLineDetails, "actions">[];
  /** Define a predefined sort order **/
  initialSort?: SortingState;
  /** Whether to show client-side pagination */
  pagination?: boolean;
  /** Decide whether to enable or disable actions **/
  actionable?: boolean;
  /** Optional rows per page for client-side pagination */
  initialRowsPerPage?: number;
}
const WeightReportsTable: FC<MatchWeightReportsTableProps> = ({
  visibleColumns,
  initialSort = null,
  pagination = true,
  actionable: _ = true,
  initialRowsPerPage = 15,
}) => {
  const { t } = useTranslation();
  const { currentCompany } = useAuth();
  const [selectedWeightLineId, setSelectedWeightLineId] = useState<WeightReportLineDetails | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(initialRowsPerPage);
  const [totalItems, setTotalItems] = useState<number>(0);

  const { data: weightReportLines, isLoading: isLoadingWeightReportLines } = useGetWeightReportLines(
    currentPage - 1,
    rowsPerPage,
    currentCompany?.companyId as number,
    null,
    {
      enabled: !!currentCompany?.companyId,
    }
  );

  useEffect(() => {
    if (weightReportLines) {
      setTotalItems(weightReportLines.total);
    }
  }, [weightReportLines]);

  const baseColumns: ColumnDef<WeightReportLineDetails>[] = useMemo(
    () => [
      {
        header: ({ column }) => <SortableHeader column={column} label={t("product:product")} />,
        id: "article",
        accessorKey: "article",
        cell: ({ row }) => {
          return <Text>{row.original.article || "-"}</Text>;
        },
      },
      {
        header: ({ column }) => (
          <SortableHeader column={column} label={t("weightReports:columnsTranslation.customerName")} />
        ),
        id: "customerName",
        accessorKey: "customerName",
        cell: ({ row }) => {
          return <Text>{row.original.customerName || "-"}</Text>;
        },
      },
      {
        header: ({ column }) => (
          <SortableHeader ta="right" column={column} label={t("weightReports:columnsTranslation.netWeight")} />
        ),
        id: "netWeight",
        accessorKey: "netWeight",
        cell: ({ row }) => {
          return (
            <Text ta="right">
              <NumberFormatter value={row.original.netWeight} suffix={` ${row.original.unit || ""}`} />
            </Text>
          );
        },
      },
      {
        header: ({ column }) => (
          <SortableHeader column={column} label={t("weightReports:columnsTranslation.weightDate")} />
        ),
        id: "weightDate",
        accessorKey: "weightDate",
        cell: ({ row }) => {
          return row.original.weightDate ? dayjs(row.original.weightDate).format("DD.MM.YYYY") : <Text>-</Text>;
        },
      },
      {
        header: ({ column }) => (
          <SortableHeader column={column} label={t("weightReports:columnsTranslation.weightTime")} />
        ),
        id: "weightTime",
        accessorKey: "weightTime",
        cell: ({ row }) => {
          return <Text>{row.original.weightTime || "-"}</Text>;
        },
      },
      {
        header: ({ column }) => (
          <SortableHeader column={column} label={t("weightReports:columnsTranslation.truckNumber")} />
        ),
        id: "truckNumber",
        accessorKey: "truckNumber",
        cell: ({ row }) => {
          return <Text>{row.original.truckNumber || "-"}</Text>;
        },
      },
      {
        header: ({ column }) => (
          <SortableHeader column={column} label={t("weightReports:columnsTranslation.matchedOrderId")} />
        ),
        id: "matchedOrderId",
        accessorKey: "matchedOrderId",
        meta: {
          breakpoint: "mantine-visible-from-md",
        },
        cell: ({ row }) => {
          return <Text>{row.original.matchedOrderId || "-"}</Text>;
        },
      },
      {
        header: ({ column }) => (
          <SortableHeader column={column} label={t("weightReports:columnsTranslation.matchStatus")} />
        ),
        id: "matchStatus",
        accessorKey: "matchStatus",
        meta: {
          breakpoint: "",
        },
        cell: ({ row }) => {
          return <MatchStatusLabel status={row.original.matchStatus} />;
        },
      },
      {
        header: () => <></>,
        id: "actions",
        size: 50,
        minSize: 50,
        maxSize: 50,
        cell: ({ row }) => {
          return (
            <Group justify={"flex-end"} w={"100%"}>
              <Menu offset={0}>
                <Menu.Target>
                  <span style={{ textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                    <Group align={"center"} justify={"center"}>
                      <SVGAsset name="dotdotdot-grey" />
                    </Group>
                  </span>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    style={{ width: "auto" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedWeightLineId(row.original);
                    }}
                  >
                    <Group>{t("actions:viewDetails")}</Group>
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
          );
        },
      },
    ],
    [t]
  );
  const columns = useVisibleColumns<WeightReportLineDetails, "actions">(baseColumns, visibleColumns);

  return (
    <>
      <DataTable
        data={weightReportLines?.items ?? []}
        columns={columns}
        isLoading={isLoadingWeightReportLines}
        mobileHeaderCell={1}
        showPagination={pagination}
        useServerSidePagination={true}
        totalItems={totalItems}
        activePage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={(page) => setCurrentPage(page)}
        onRowsPerPageChange={(newRows) => {
          setRowsPerPage(newRows);
          setCurrentPage(1);
        }}
        initialSort={initialSort ?? undefined}
        onRowClick={(row) => setSelectedWeightLineId(row.original)}
      />
      <WeightReportDrawer selectedWeightLine={selectedWeightLineId} setSelectedWeightLine={setSelectedWeightLineId} />
    </>
  );
};

export default WeightReportsTable;
